import {createContext, useContext, useEffect, useReducer} from 'react';
import {useParams} from 'react-router-dom';
import {fetchArrivalSettings, fetchCountries, fetchDropinCategories, fetchVisitReasons} from 'src/services/api/api';
import {NotFoundError} from 'src/services/api/implementations/fetch';
import {
    ArrivalSettingsResponse,
    ReasonFilterObject,
    AllowedAge,
    DropinCategory,
    DropinType,
    Country,
    DropinArrivalPerson
} from 'src/services/api/types/types';
import {useTranslation} from 'react-i18next';
import {Destinations} from './BookingContext';

export enum DataActions {
    SET_LOADING = 'SET_LOADING',
    SET_ERROR = 'SET_ERROR',
    SET_SETTINGS = 'SET_SETTINGS',
    SET_REASON_DATA = 'SET_REASON_DATA',
    SET_PEOPLE = 'SET_PEOPLE',
    NEXT_STEP = 'NEXT_STEP',
    SET_STEP = 'SET_STEP',
    SET_CATEGORIES = 'SET_CATEGORIES',
    SET_VISIT_REASONS = 'SET_VISIT_REASONS',
    SET_PERSON_HEALTH_DECL = 'SET_PERSON_HEALTH_DECL',
    SET_COUNTRIES = 'SET_COUNTRIES',
    SET_TRIP = 'SET_TRIP'
}

type Action = {
    type: DataActions;
    value?:
        | boolean
        | null
        | Error
        | number
        | ArrivalSettingsResponse
        | ReasonState
        | ModalStep
        | Array<Person>
        | Array<DropinCategory>
        | Array<ReasonFilterObject>
        | {identity: string; healthDecl: Person['healthDecl']}
        | {trip?: Destinations; tripDepartureDate?: Date | null}
        | Array<Country>;
};

export type ModalStep = 'initial' | 'people' | 'hd' | 'completed';

type Dispatch = (action: Action) => void;

type State = {
    loading: boolean;
    settings: ArrivalSettingsResponse | null;
    error: Error | null;
    clinicId: number;
    reasonState: ReasonState;
    step: ModalStep;
    steps: Array<ModalStep>;
    persons: Array<Person>;
    categories: Array<DropinCategory>;
    visitReasons: Array<ReasonFilterObject>;
    healthDeclarationAnswers: PersonAnswers | null;
    trip?: Destinations;
    tripDepartureDate?: Date | null;
    countries: Array<Country>;
};
type ArrivalProviderProps = {children: React.ReactNode};
interface ReasonState {
    category: DropinCategory | null;
    numberPatients: number | null;
    appointmentType: DropinType | null;
    healthDeclarationDefinitionId: number | null;
    allowedAge: AllowedAge | null;
}
export interface Person {
    id?: string;
    identity: string;
    identityValid: boolean;
    givenName: string;
    familyName: string;
    age: number | undefined;
    gender: 'male' | 'female' | 'unknown';
    healthDecl?: {
        definitionId: number;
        answers: [
            {
                questionId: number;
                answer: string;
            }
        ];
        trip?: {
            date: string;
            destinations: [
                {
                    countryId: number;
                    duration: number;
                }
            ];
        };
    };
    contactInfo?: {
        email: string;
        mobile: string;
        marketing?: boolean;
    };
}

export interface PersonAnswers {
    answers: {
        [identity: string]: HealthDeclarationAnswers;
    };
}

export interface HealthDeclarationAnswers {
    [id: number]: boolean | null;
}

const allSteps: Array<ModalStep> = ['initial', 'people', 'hd', 'completed'];

const nextStep = (state: State): ModalStep => {
    const {step, steps} = state;
    const currStepIndex = steps.indexOf(step);
    const nextStep = steps[currStepIndex + 1];

    return nextStep || null;
};

const getSteps = (state: State, allSteps: Array<ModalStep>) => {
    const {reasonState} = state;

    if (reasonState.appointmentType === null) {
        return allSteps;
    }
    return allSteps.filter((step) => {
        if (reasonState.appointmentType?.healthDeclarationRequired === false && step == 'hd') {
            return false;
        }

        return true;
    });
};

const ArrivalContext = createContext<{dataState: State; dispatch: Dispatch} | undefined>(undefined);

function ArrivalReducer(state: State, action: Action): State {
    switch (action.type) {
        case DataActions.SET_LOADING:
            return {...state, loading: action.value as boolean};
        case DataActions.SET_ERROR:
            return {...state, error: action.value as Error};
        case DataActions.SET_SETTINGS:
            return {...state, settings: action.value as ArrivalSettingsResponse};
        case DataActions.SET_REASON_DATA: {
            const newState = {...state, reasonState: action.value as ReasonState};
            const newSteps = getSteps(newState, allSteps);
            return {...newState, steps: newSteps};
        }
        case DataActions.SET_PEOPLE: {
            return {...state, persons: action.value as Array<Person>};
        }
        case DataActions.NEXT_STEP: {
            return {...state, step: nextStep(state)};
        }
        case DataActions.SET_STEP: {
            return {...state, step: action.value as ModalStep};
        }
        case DataActions.SET_CATEGORIES: {
            return {...state, categories: action.value as Array<DropinCategory>};
        }
        case DataActions.SET_VISIT_REASONS: {
            return {...state, visitReasons: action.value as Array<ReasonFilterObject>};
        }
        case DataActions.SET_PERSON_HEALTH_DECL: {
            if (
                typeof action.value === 'object' &&
                action.value !== null &&
                'identity' in action.value &&
                'healthDecl' in action.value
            ) {
                const {identity, healthDecl} = action.value;

                return {
                    ...state,
                    persons: state.persons.map((person) =>
                        person.identity === identity ? {...person, healthDecl} : person
                    )
                };
            }
            return state;
        }
        case DataActions.SET_COUNTRIES:
            return {...state, countries: action.value as Array<Country>};
        case DataActions.SET_TRIP: {
            if (
                typeof action.value === 'object' &&
                action.value !== null &&
                'trip' in action.value &&
                'tripDepartureDate' in action.value
            ) {
                const {trip, tripDepartureDate} = action.value;

                return {
                    ...state,
                    trip: trip,
                    tripDepartureDate: tripDepartureDate
                };
            }
            return state;
        }
        default:
            throw new Error(`Unhandled action type: ${action.type}`);
    }
}

function ArrivalProvider({children}: ArrivalProviderProps) {
    const {t, i18n} = useTranslation();
    const {clinicId} = useParams<{clinicId: string}>();
    const clinicIdNum = parseInt(clinicId || '', 10);

    const [dataState, dispatch] = useReducer(ArrivalReducer, {
        loading: true,
        error: null,
        settings: null,
        clinicId: clinicIdNum,
        reasonState: {
            category: null,
            numberPatients: null,
            appointmentType: null,
            healthDeclarationDefinitionId: null,
            allowedAge: null
        },
        step: 'initial',
        steps: allSteps,
        persons: [],
        categories: [],
        visitReasons: [],
        healthDeclarationAnswers: null,
        countries: []
    });

    useEffect(() => {
        if (Number.isNaN(clinicIdNum)) {
            dispatch({type: DataActions.SET_LOADING, value: false});
            dispatch({type: DataActions.SET_ERROR, value: new NotFoundError('No such clinic')});
            return;
        }

        const loadData = async () => {
            try {
                const [settings, categories, visitReasons, countries] = await Promise.all([
                    fetchArrivalSettings(clinicIdNum),
                    fetchDropinCategories(clinicIdNum),
                    fetchVisitReasons(),
                    fetchCountries()
                ]);

                dispatch({type: DataActions.SET_SETTINGS, value: settings});
                dispatch({type: DataActions.SET_LOADING, value: false});
                dispatch({type: DataActions.SET_CATEGORIES, value: categories});
                dispatch({type: DataActions.SET_VISIT_REASONS, value: visitReasons});
                dispatch({type: DataActions.SET_COUNTRIES, value: countries});
            } catch (error) {
                dispatch({type: DataActions.SET_LOADING, value: false});
                dispatch({type: DataActions.SET_ERROR, value: error as Error});
            }
        };

        loadData();
    }, [clinicIdNum, i18n.language]);

    const value = {dataState, dispatch};
    return <ArrivalContext.Provider value={value}>{children}</ArrivalContext.Provider>;
}

function useArrivalContext() {
    const context = useContext(ArrivalContext);
    if (context === undefined) {
        throw new Error('useDataContext must be used within a UiProvider');
    }

    return context;
}

function toDropinArrivalPerson(person: Person) {
    const contactInfo = person.contactInfo
        ? {
              mobile: person.contactInfo.mobile.length > 0 ? person.contactInfo.mobile : undefined,
              email: person.contactInfo.email.length > 0 ? person.contactInfo.email : undefined,
              marketing: person.contactInfo.marketing,
              preferredContact: person.contactInfo.email.length > 0 ? 'both' : 'sms'
          }
        : undefined;
    return {
        identity: person.identity,
        givenName: person.givenName,
        familyName: person.familyName,
        healthDecl: person.healthDecl,
        contactInfo: contactInfo
    } as DropinArrivalPerson;
}

export {ArrivalProvider, useArrivalContext, toDropinArrivalPerson};
